import React, { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';

import {
  Configure,
  Hits,
  InfiniteHits,
  Pagination,
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  usePagination,
  useSortBy,
} from 'react-instantsearch-hooks-web';
import { Category } from 'shared/types/product/Category';
import Wrapper from 'components/HOC/wrapper';
import { mapProduct } from 'helpers/algolia/map-product';
import { useFormat } from 'helpers/hooks/useFormat';
import AccumalativeTrace from './components/accumalative-trace';
import Breadcrumbs from './components/breadcrumb';
import CurrentRefinements from './components/current-refinements';
import DesktopFacets from './components/desktop-facets';
import MobileFacets from './components/mobile-facets';
import SearchHeader from './components/search-header';
import { FacetConfiguration, ILCategoryFacetFilters, IndependentLivingCategoriesFilters } from './types';
import ProductTile from '../product-tile';
import Search from 'components/commercetools-ui/atoms/search-algolia';
import DesktopSortFacets from './components/desktop-facets/sort-facets';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, tablet } from 'helpers/utils/screensizes';
import LocalizedIndex, { useLocalizedIndex } from 'providers/algolia/localized-index';
import ContentSearchItem from 'components/commercetools-ui/atoms/search-algolia/content-search';
import CustomStats from 'components/commercetools-ui/atoms/search-algolia/customstats';
import { localeMapping } from 'helpers/constants/localeMapping';
import Head from 'components/microhead';
import { Tiempos } from 'fonts';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import { CATEGORY_IDs } from 'helpers/constants/products';
interface Props {
  noContentSuggestions?: any;
  slug?: string;
  searchQuery?: string;
  categories: Category[];
  facetsConfiguration: Record<string, FacetConfiguration>;
  suggestions?: any;  
}
interface NoResultProps {
  fallback: ReactNode;
}
function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }
  return children;
}
function NoResults({ results, suggestions, searchQuery }: any) {
  const { formatMessage } = useFormat({ name: 'common' });
  const router = useRouter();

  const handleSuggestion = (suggestion: string) => {
    router.push(`/search?query=${encodeURIComponent(suggestion)}`, { shallow: true });
  };
  return (
    <div>
      {searchQuery && results?.nbHits === 0 ? (
        <div className="m-auto max-w-[900px] text-center lg:!text-left">
          <p className={`results-zero no-result-container mb-2`}>No products found</p>
          <div className="d-inline">
            {formatMessage({
              id: 'no.resultfound.query',
              defaultMessage: 'No search results are found for your query:',
            })}
            <span className="font-bold"> &quot;{searchQuery ? searchQuery : ''}&quot;</span>.
            {suggestions.length > 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'did.you.mean',
                  defaultMessage: 'Did you mean?',
                })}{' '}
                {suggestions.map((suggestion: any, index: any) => (
                  <>
                    <span
                      role="button"
                      onClick={() => handleSuggestion(suggestion?.query)}
                      key={index}
                      className="text-decoration-underline font-medium"
                    >
                      {suggestion?.query}
                    </span>
                    <span className="text-decoration-none">{index < suggestions.length - 1 && ', '}</span>
                  </>
                ))}
              </p>
            )}
            {suggestions.length === 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'search.popular.categories',
                  defaultMessage: 'You might want to search for popular categories',
                })}{' '}
                {['Tapware', 'Sanitaryware', 'Contura', 'Urbane II'].map((cat: any, index: any) => (
                  <>
                    <span
                      role="button"
                      onClick={() => handleSuggestion(cat)}
                      key={index}
                      className="text-decoration-underline font-medium"
                    >
                      {cat}
                    </span>
                    <span className="text-decoration-none">{index < cat.length - 1 && ', '}</span>
                  </>
                ))}
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className={`${results?.nbHits === 0 && 'results-zero'} no-result-container`}>No products found</p>
      )}
    </div>
  );
}
const ProductListAlgolia: React.FC<Props> = ({
  slug,
  searchQuery,
  categories,
  facetsConfiguration,
  noContentSuggestions,
  suggestions
}) => {
  const [isTabletSize] = useMediaQuery(desktop);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { locale, slug: categorySlug } = useParams();
  const { refine } = usePagination();
  const { refine: clearAllRefinements } = useClearRefinements();
  const { indexName: productsIndex } = useLocalizedIndex({ type: 'products' });
  const localeUpperCase = locale.toUpperCase();
  const { refine: clearSortRefinement } = useSortBy({
    items: [
      {
        label: formatProductMessage({ id: 'new-products', defaultMessage: 'New' }),
        value: productsIndex,
      },
      {
        label: formatProductMessage({ id: 'nameAsc', defaultMessage: 'Name A-Z' }),
        value: productsIndex.replace('-main', '') + '_name_asc',
      },
      {
        label: formatProductMessage({ id: 'nameDsc', defaultMessage: 'Name Z-A' }),
        value: productsIndex.replace('-main', '') + '_name_desc',
      },
    ],
  });
  const { items } = useCurrentRefinements();
  const { hits, results } = useHits();
  const hitsLength = hits.length;
  const totalHits = results ? results.nbHits : 0;
  const localeKey = localeMapping[locale];
  const currentPage = results ? results.page : 0;
  const totalPages = results ? results.nbPages : 0;

  const isShower = categorySlug?.includes('showers');
  const isTapwareAccessories = categorySlug?.includes('tapware-and-accessories');
  const isflexispray = categorySlug?.includes('flexispray');


  // const isKitchenLaundry = categorySlug?.includes('kitchen-laundry');
  // const isBathroom = categorySlug?.includes('bathroom');
  // const localeKey = localeMapping[locale];
  // const isIndependentLiving = categorySlug?.includes('independent-living');
  


  useEffect(() => {
    refine(0);
    clearAllRefinements();
    clearSortRefinement(productsIndex);
  }, [searchQuery]);


   const category = useMemo(() => categories.find((category) => category.slug === slug), [categories, slug]);

  
  const getFilterQuery = () => {
    //Algolia queries for "Shower"
    if (isflexispray && slug === 'flexispray')
      return `searchableAttributes.brand:"Flexispray"`
    //Algolia queries for "Shower"
    //--------------------------------------------------------------------------------
    if (isShower && slug === 'rail-showers')
      return `categoryKeys.${localeKey}:"Showers" AND categoryKeys.${localeKey}:"Shower Systems" AND searchableAttributes.product-types:"Shower on Rails"`;
    if (isShower && slug === 'overhead-showers')
      return `categoryKeys.${localeKey}:"Showers" AND categoryKeys.${localeKey}:"Shower Heads Outlets" AND (searchableAttributes.product-types:"Shower System With Overheads" OR  searchableAttributes.product-types:"Overhead Showers")`;
    if (isShower && slug === 'shower-systems')
      return `categoryKeys.${localeKey}:"Showers" AND categoryKeys.${localeKey}:"Shower Systems" AND searchableAttributes.product-types:"Shower System With Overheads"`;
    if (isShower && slug === 'wall-showers')
      return `categoryKeys.${localeKey}:"Showers" AND categoryKeys.${localeKey}:"Shower Heads Outlets" AND (searchableAttributes.product-types:"Adjustable Showers" OR searchableAttributes.product-types:"Fixed Showers")`;
    if (isShower && slug === 'shower-handsets')
      return `categoryKeys.${localeKey}:"Showers" AND categoryKeys.${localeKey}:"Shower Heads Outlets" AND searchableAttributes.product-types:"Hand Shower Pieces"`;
    if (isShower && slug === 'hand-showers')
      return `categoryKeys.${localeKey}:"Showers" AND categoryKeys.${localeKey}:"Shower Heads Outlets" AND searchableAttributes.product-types:"Shower with Brackets"`;    
    if (isShower && slug === 'spares')
      // return `searchableAttributes.product-types:"Bath/Shower Trim Kits" OR searchableAttributes.product-types:"Bath/Shower Trim Kits with Diverters" OR searchableAttributes.product-types:"Flexible Connectors" OR searchableAttributes.product-types:"Handles/Levers" OR searchableAttributes.product-types:"Aerators & Regulators" OR searchableAttributes.product-types:"Backing Plates" OR searchableAttributes.product-types:"Body Seal Kits" OR searchableAttributes.product-types:"Cartridges" OR searchableAttributes.product-types:"Divertors" OR searchableAttributes.product-types:"Flexible Tails" OR searchableAttributes.product-types:"Retractable Spray" OR searchableAttributes.product-types:"Sink Mixer Service Kits" OR searchableAttributes.product-types:"Thermostatic" OR searchableAttributes.product-types:"Fixing Kits" OR searchableAttributes.product-types:"Aerators & Housing" OR searchableAttributes.product-types:"Breech/body" OR searchableAttributes.product-types:"Buttons & Bezels" OR searchableAttributes.product-types:"Cd Spindles" OR searchableAttributes.product-types:"Dress Rings" OR searchableAttributes.product-types:"Electronic" OR searchableAttributes.product-types:"Fixing Kits And Adaptions" OR searchableAttributes.product-types:"Jumper Valves" OR searchableAttributes.product-types:"Jv Spindles" OR searchableAttributes.product-types:"Tap Flanges" OR searchableAttributes.product-types:"Termal Barriers" OR searchableAttributes.product-types:"Wall Bath/Basin Trim Kits" OR searchableAttributes.product-types:"Shower Spares"`;
      return `searchableAttributes.product-types:"Shower Spares"`
   
    if (isShower && category && category?.name === "Showers")
      return `categoryKeys.${localeKey}:"Showers" AND (categoryKeys.${localeKey}:"Shower Systems" OR categoryKeys.${localeKey}:"Shower Heads Outlets") AND (searchableAttributes.product-types:"Shower on Rails" OR searchableAttributes.product-types:"Shower System With Overheads" OR  searchableAttributes.product-types:"Overhead Showers" OR searchableAttributes.product-types:"Shower System With Overheads" OR searchableAttributes.product-types:"Adjustable Showers" OR searchableAttributes.product-types:"Fixed Showers" OR searchableAttributes.product-types:"Hand Shower Pieces" OR searchableAttributes.product-types:"Shower with Brackets" OR searchableAttributes.product-types:"Bath/Shower Trim Kits" OR searchableAttributes.product-types:"Bath/Shower Trim Kits with Diverters" OR searchableAttributes.product-types:"Flexible Connectors" OR searchableAttributes.product-types:"Handles/Levers" OR searchableAttributes.product-types:"Aerators & Regulators" OR searchableAttributes.product-types:"Backing Plates" OR searchableAttributes.product-types:"Body Seal Kits" OR searchableAttributes.product-types:"Cartridges" OR searchableAttributes.product-types:"Divertors" OR searchableAttributes.product-types:"Flexible Tails" OR searchableAttributes.product-types:"Retractable Spray" OR searchableAttributes.product-types:"Sink Mixer Service Kits" OR searchableAttributes.product-types:"Thermostatic" OR searchableAttributes.product-types:"Fixing Kits" OR searchableAttributes.product-types:"Aerators & Housing" OR searchableAttributes.product-types:"Breech/body" OR searchableAttributes.product-types:"Buttons & Bezels" OR searchableAttributes.product-types:"Cd Spindles" OR searchableAttributes.product-types:"Dress Rings" OR searchableAttributes.product-types:"Electronic" OR searchableAttributes.product-types:"Fixing Kits And Adaptions" OR searchableAttributes.product-types:"Jumper Valves" OR searchableAttributes.product-types:"Jv Spindles" OR searchableAttributes.product-types:"Tap Flanges" OR searchableAttributes.product-types:"Termal Barriers" OR searchableAttributes.product-types:"Wall Bath/Basin Trim Kits" OR searchableAttributes.product-types:"Shower Spares")`;
    //--------------------------------------------------------------------------------

    //Algolia queries for "Tapware & Accessories"
    //--------------------------------------------------------------------------------
    if (isTapwareAccessories && slug === 'spouts')
      return `categoryKeys.${localeKey}:"Tapware" AND categoryKeys.${localeKey}:"Outlets Spouts" AND (searchableAttributes.product-types:"Basin Outlets" OR searchableAttributes.product-types:"Wall Bath Outlets" OR searchableAttributes.product-types:"Hob Outlets" OR searchableAttributes.product-types:"Bath Outlets") `;
    if (isTapwareAccessories && slug === 'sink-mixers')
      return `categoryKeys.${localeKey}:"Tapware" AND (categoryKeys.${localeKey}:"Kitchen Tapware" OR categoryKeys.${localeKey}:"Laundry Tapware" OR categoryKeys.${localeKey}:"Care Tapware") AND (searchableAttributes.product-types:"Sink Mixers" OR searchableAttributes.product-types:"Care Sink Mixers" OR searchableAttributes.product-types:"Laundry Sink Mixer" OR searchableAttributes.product-types:"Retractable Sink Mixers" OR searchableAttributes.product-types:"Wall Sink Mixers")`;
    if (isTapwareAccessories && slug === 'accessories')
      //return `categoryKeys.${localeKey}:"Tapware" AND (categoryKeys.${localeKey}:"Care Accessories" OR categoryKeys.${localeKey}:"Bathroom Holders" OR categoryKeys.${localeKey}:"Shelves/Cabinets" OR categoryKeys.${localeKey}:"Soap Accessories" OR categoryKeys.${localeKey}:"Toilet Roll Accessories" OR categoryKeys.${localeKey}:"Towel Accessories") AND (searchableAttributes.product-types:"Grab Rails" OR searchableAttributes.product-types:"Single Shelves" OR searchableAttributes.product-types:"Cabinets" OR searchableAttributes.product-types:"Soap Baskets" OR searchableAttributes.product-types:"Soap Dispensers" OR searchableAttributes.product-types:"Soap Holders" OR searchableAttributes.product-types:"Toothbrush Holders" OR searchableAttributes.product-types:"Tumbler Holders" OR searchableAttributes.product-types:"Toilet Roll Holders" OR searchableAttributes.product-types:"Double Robe Hook" OR searchableAttributes.product-types:"Double Towel Rails" OR searchableAttributes.product-types:"Single Robe Hook" OR searchableAttributes.product-types:"Single Towel Rails" OR searchableAttributes.product-types:"Towel Holders" OR searchableAttributes.product-types:"Towel Racks" OR searchableAttributes.product-types:"Towel Rings")`;
      return `searchableAttributes.product-types:"Grab Rails" OR searchableAttributes.product-types:"Single Shelves" OR searchableAttributes.product-types:"Cabinets" OR searchableAttributes.product-types:"Soap Baskets" OR searchableAttributes.product-types:"Soap Dispensers" OR searchableAttributes.product-types:"Soap Holders" OR searchableAttributes.product-types:"Toothbrush Holders" OR searchableAttributes.product-types:"Tumbler Holders" OR searchableAttributes.product-types:"Toilet Roll Holders" OR searchableAttributes.product-types:"Double Robe Hook" OR searchableAttributes.product-types:"Double Towel Rails" OR searchableAttributes.product-types:"Single Robe Hook" OR searchableAttributes.product-types:"Single Towel Rails" OR searchableAttributes.product-types:"Towel Holders" OR searchableAttributes.product-types:"Towel Racks" OR searchableAttributes.product-types:"Towel Rings"`;
    if (isTapwareAccessories && slug === 'basin-mixers')
      return `categoryKeys.${localeKey}:"Tapware" AND categoryKeys.${localeKey}:"Basin Mixers" AND (searchableAttributes.product-types:"Care Basin Mixers" OR searchableAttributes.product-types:"Electronic Basin Mixers" OR searchableAttributes.product-types:"Wall Basin Mixers" OR searchableAttributes.product-types:"Standard Basin Mixers" OR searchableAttributes.product-types:"Mid Basin Mixers" OR searchableAttributes.product-types:"Tower Basin Mixers")`;
    if (isTapwareAccessories && slug === 'tapware-sets')
      return `categoryKeys.${localeKey}:"Tapware" AND (categoryKeys.${localeKey}:"Three piece Tapware" OR categoryKeys.${localeKey}:"Tapware Assemblies") AND (searchableAttributes.product-types:"Basins Sets" OR searchableAttributes.product-types:"Wall Basin Sets" OR searchableAttributes.product-types:"Bath/Shower Sets" OR searchableAttributes.product-types:"Hob Bath Sets" OR searchableAttributes.product-types:"Shower Sets" OR searchableAttributes.product-types:"Wall Bath Sets" OR searchableAttributes.product-types:"Stop Taps" OR searchableAttributes.product-types:"Electronic Sets" OR searchableAttributes.product-types:"Double Robe Hook" OR searchableAttributes.product-types:"Double Towel Rails" OR searchableAttributes.product-types:"Single Robe Hook" OR searchableAttributes.product-types:"Single Towel Rails" OR searchableAttributes.product-types:"Towel Holders" OR searchableAttributes.product-types:"Towel Racks")`;
    if (isTapwareAccessories && slug === 'shower-mixers')
      return `categoryKeys.${localeKey}:"Tapware" AND categoryKeys.${localeKey}:"Bath Shower Mixers" AND (searchableAttributes.product-types:"Shower Mixers" OR searchableAttributes.product-types:"Shower Mixers with Diverter" OR searchableAttributes.product-types:"Wall Bath Mixers" OR searchableAttributes.product-types:"Bidet Mixers" OR searchableAttributes.product-types:"Freestanding Bath Mixers")`;    
    if (isTapwareAccessories && slug === 'spares')
      //return `searchableAttributes.product-types:"Bath/Shower Trim Kits" OR searchableAttributes.product-types:"Bath/Shower Trim Kits with Diverters" OR searchableAttributes.product-types:"Flexible Connectors" OR searchableAttributes.product-types:"Handles/Levers" OR searchableAttributes.product-types:"Aerators & Regulators" OR searchableAttributes.product-types:"Backing Plates" OR searchableAttributes.product-types:"Body Seal Kits" OR searchableAttributes.product-types:"Cartridges" OR searchableAttributes.product-types:"Divertors" OR searchableAttributes.product-types:"Flexible Tails" OR searchableAttributes.product-types:"Retractable Spray" OR searchableAttributes.product-types:"Sink Mixer Service Kits" OR searchableAttributes.product-types:"Thermostatic" OR searchableAttributes.product-types:"Fixing Kits" OR searchableAttributes.product-types:"Aerators & Housing" OR searchableAttributes.product-types:"Breech/body" OR searchableAttributes.product-types:"Buttons & Bezels" OR searchableAttributes.product-types:"Cd Spindles" OR searchableAttributes.product-types:"Dress Rings" OR searchableAttributes.product-types:"Electronic" OR searchableAttributes.product-types:"Fixing Kits And Adaptions" OR searchableAttributes.product-types:"Jumper Valves" OR searchableAttributes.product-types:"Jv Spindles" OR searchableAttributes.product-types:"Tap Flanges" OR searchableAttributes.product-types:"Termal Barriers" OR searchableAttributes.product-types:"Wall Bath/Basin Trim Kits" OR searchableAttributes.product-types:"Shower Spares"`;
      return `searchableAttributes.product-types:"Tapware spares other"`

    if (isTapwareAccessories && slug && slug === "tapware-and-accessories")
      return `categoryKeys.${localeKey}:"Tapware" AND (categoryKeys.${localeKey}:"Bath Shower Mixers" OR categoryKeys.${localeKey}:"Kitchen Tapware" OR categoryKeys.${localeKey}:"Laundry Tapware" OR categoryKeys.${localeKey}:"Care Tapware" OR categoryKeys.${localeKey}:"Care Accessories" OR categoryKeys.${localeKey}:"Bathroom Holders" OR categoryKeys.${localeKey}:"Shelves/Cabinets" OR categoryKeys.${localeKey}:"Soap Accessories" OR categoryKeys.${localeKey}:"Toilet Roll Accessories" OR categoryKeys.${localeKey}:"Towel Accessories" OR categoryKeys.${localeKey}:"Basin Mixers" OR categoryKeys.${localeKey}:"Three piece Tapware" OR categoryKeys.${localeKey}:"Tapware Assemblies") AND (searchableAttributes.product-types:"Basin Outlets" OR searchableAttributes.product-types:"Bath Outlets" OR searchableAttributes.product-types:"Bath Outlets" OR searchableAttributes.product-types:"Sink Outlets" OR searchableAttributes.product-types:"Sink Mixers" OR searchableAttributes.product-types:"Care Sink Mixers" OR searchableAttributes.product-types:"Laundry Sink Mixer" OR searchableAttributes.product-types:"Retractable Sink Mixers" OR searchableAttributes.product-types:"Wall Sink Mixers" OR searchableAttributes.product-types:"Grab Rails" OR searchableAttributes.product-types:"Single Shelves" OR searchableAttributes.product-types:"Cabinets" OR searchableAttributes.product-types:"Soap Baskets" OR searchableAttributes.product-types:"Soap Dispensers" OR searchableAttributes.product-types:"Soap Holders" OR searchableAttributes.product-types:"Toothbrush Holders" OR searchableAttributes.product-types:"Tumbler Holders" OR searchableAttributes.product-types:"Toilet Roll Holders" OR searchableAttributes.product-types:"Double Robe Hook" OR searchableAttributes.product-types:"Double Towel Rails" OR searchableAttributes.product-types:"Single Robe Hook" OR searchableAttributes.product-types:"Single Towel Rails" OR searchableAttributes.product-types:"Towel Holders" OR searchableAttributes.product-types:"Towel Racks" OR searchableAttributes.product-types:"Towel Rings" OR searchableAttributes.product-types:"Care Basin Mixers" OR searchableAttributes.product-types:"Electronic Basin Mixers" OR searchableAttributes.product-types:"Wall Basin Mixers" OR searchableAttributes.product-types:"Standard Basin Mixers" OR searchableAttributes.product-types:"Mid Basin Mixers" OR searchableAttributes.product-types:"Tower Basin Mixers" OR searchableAttributes.product-types:"Basins Sets" OR searchableAttributes.product-types:"Wall Basin Sets" OR searchableAttributes.product-types:"Bath/Shower Sets" OR searchableAttributes.product-types:"Hob Bath Sets" OR searchableAttributes.product-types:"Shower Sets" OR searchableAttributes.product-types:"Wall Bath Sets" OR searchableAttributes.product-types:"Stop Taps" OR searchableAttributes.product-types:"Electronic Sets" OR searchableAttributes.product-types:"Double Robe Hook" OR searchableAttributes.product-types:"Double Towel Rails" OR searchableAttributes.product-types:"Single Robe Hook" OR searchableAttributes.product-types:"Single Towel Rails" OR searchableAttributes.product-types:"Towel Holders" OR searchableAttributes.product-types:"Towel Racks" OR searchableAttributes.product-types:"Shower Mixers" OR searchableAttributes.product-types:"Shower Mixers with Diverter" OR searchableAttributes.product-types:"Wall Bath Mixers" OR searchableAttributes.product-types:"Bidet Mixers" OR searchableAttributes.product-types:"Freestanding Bath Mixers" OR searchableAttributes.product-types:"Bath/Shower Trim Kits" OR searchableAttributes.product-types:"Bath/Shower Trim Kits with Diverters" OR searchableAttributes.product-types:"Flexible Connectors" OR searchableAttributes.product-types:"Handles/Levers" OR searchableAttributes.product-types:"Aerators & Regulators" OR searchableAttributes.product-types:"Backing Plates" OR searchableAttributes.product-types:"Body Seal Kits" OR searchableAttributes.product-types:"Cartridges" OR searchableAttributes.product-types:"Divertors" OR searchableAttributes.product-types:"Flexible Tails" OR searchableAttributes.product-types:"Retractable Spray" OR searchableAttributes.product-types:"Sink Mixer Service Kits" OR searchableAttributes.product-types:"Thermostatic" OR searchableAttributes.product-types:"Fixing Kits" OR searchableAttributes.product-types:"Aerators & Housing" OR searchableAttributes.product-types:"Breech/body" OR searchableAttributes.product-types:"Buttons & Bezels" OR searchableAttributes.product-types:"Cd Spindles" OR searchableAttributes.product-types:"Dress Rings" OR searchableAttributes.product-types:"Electronic" OR searchableAttributes.product-types:"Fixing Kits And Adaptions" OR searchableAttributes.product-types:"Jumper Valves" OR searchableAttributes.product-types:"Jv Spindles" OR searchableAttributes.product-types:"Tap Flanges" OR searchableAttributes.product-types:"Termal Barriers" OR searchableAttributes.product-types:"Wall Bath/Basin Trim Kits" OR searchableAttributes.product-types:"Shower Spares")`;
    //--------------------------------------------------------------------------------

  };



  const getTitleFromSlug = () => {

    //List for Showers
    if (slug === 'showers') return 'Showers';
    if (slug === 'rail-showers') return 'Rail Showers';
    if (slug === 'overhead-showers') return 'Overhead Showers';
    if (slug === 'twin-shower-systems') return 'Twin Shower Systems';
    if (slug === 'wall-showers') return 'Wall Showers';
    if (slug === 'shower-handsets') return 'Shower Handsets';
    if (slug === 'hand-showers') return 'Hand Showers';
    if (slug === 'spares') return 'Spares';
    if (slug === 'flexispray') return 'Flexispray';
    //List for Showers


    //List for Tapware & Accessories
    if (slug === 'tapware-and-accessories') return 'Tapware & Accessories';
    if (slug === 'tapware-sets') return 'Tapware Sets';
    if (slug === 'basin-mixers') return 'Basin Mixers';
    if (slug === 'bath-shower-mixers') return 'Bath / Shower Mixers';
    if (slug === 'shower-mixers') return 'Shower Mixers';    
    if (slug === 'sink-mixers') return 'Sink Mixers';
    if (slug === 'outlets') return 'Outlets';
    if (slug === 'accessories') return 'Accessories';
    //List for Tapware & Accessories

    return `${slug?.replaceAll('-', ' ').replace(' ', ' & ')}`;
  };

  const handleChange = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLAnchorElement) {
      const element = document.getElementById("plp-container");
      if (element) {
        window.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <>
      <div id="plp-container" className={searchQuery ? 'bg-white' : 'bg-[#EDEDED]'}>
        <Configure
          query={searchQuery ?? ''}
          filters={getFilterQuery()}
          hitsPerPage={12}
          facets={['*']}
          attributesToHighlight={[]} // Disable highlighting
          attributesToSnippet={[]} // Disable snippetting
          attributesToRetrieve={[
            "name",
            "slug",
            "categoryKeys",
            "masterVariantID",
            "searchableAttributes.IsNew",
            "variants.id",
            "variants.key",
            "variants.sku",
            "variants.images",
            "variants.attributes.colour",
            "variants.attributes.item-code",
            "variants.attributes.item-name",
          ]}
          maxValuesPerFacet={999}
        />
        {searchQuery ? (
          <div className="mt-[1rem] px-24 lg:mt-[0.75rem] lg:px-48 xl:px-60 2xl:mt-[2rem] 2xl:px-80">
            <SearchHeader
              query={searchQuery ?? ''}
              categories={categories}
              noContentSuggestions={noContentSuggestions}
            />
          </div>
        ) : (
          <>
            <div id="container-scroll" className="flex flex-col items-end">
              <h1 className={`w-full pb-32 pt-48 text-center text-32 font-medium leading-8 text-[#090909] md:text-40 2xl:pb-48 2xl:pt-80 2xl:text-48 ${Tiempos.className}`}>
                Products in {getTitleFromSlug()}
              </h1>
              <div className="px:24 mt-8 flex w-full items-center justify-between gap-48 lg:px-48 xl:px-60 2xl:px-80">
                <div className="lg:ml-[16.5rem]  2xl:ml-[23rem]">
                  <CurrentRefinements />
                </div>
                <div className="hidden lg:block">
                  <DesktopSortFacets />
                </div>
              </div>
            </div>
          </>
        )}
        <div
          className={`items-start gap-24 pt-24 md:flex md:flex-row lg:px-48 xl:px-60 2xl:gap-48 2xl:px-80 ${
            searchQuery ? '' : 'px-24 md:px-28'
          } ${results?.nbHits === 0 && items?.length ===0? 'pb-0' : 'pb-48 xl:pb-0 2xl:pb-48'}`}
        >
          <div
            className={
              searchQuery
                ? `${
                    results?.nbHits === 0&& items?.length ===0 ? '' : 'lg:block'
                  } hidden bg-white lg:mt-40  lg:min-w-[15rem]  2xl:min-w-[20rem]`
                : `${
                    results?.nbHits === 0 && items?.length ===0? '' : 'lg:block'
                  }  hidden bg-[#EDEDED] lg:block lg:min-w-[15rem] 2xl:min-w-[20rem]`
            }
          >
            <DesktopFacets facetsConfiguration={facetsConfiguration} />
          </div>
          <div className={searchQuery ? 'w-full flex-1  bg-white lg:mt-40' : 'w-full bg-[#EDEDED]'}>
            {searchQuery && (
              <h1 className="px-24 py-12 text-32 font-medium leading-8 text-[#090909] md:py-32  md:px-28 md:text-40 lg:hidden lg:py-0  2xl:text-48">
                Products
              </h1>
            )}
            {searchQuery && (
              <div className=''>
              <div id="product-suggestions" className="pb-10 md:pb-24 lg:pb-0 2xl:pb-0 xl:pb-0 flex items-start" >
                <CurrentRefinements />
                {isTabletSize ? (
                  <NoResultsBoundary fallback={<span></span>}>
                    <div className="ml-auto flex flex-col items-end">
                      <DesktopSortFacets />
                    </div>
                  </NoResultsBoundary>
                ) : (
                  <></>
                )}
                </div>
              </div>
            )}
            <div className={`${searchQuery ? 'px-24 md:px-28' : ''} ${results?.nbHits === 0 && items?.length ===0&& "hidden"}`}>
              <MobileFacets facetsConfiguration={facetsConfiguration} />
            </div>
            {isTabletSize ? (
              <>
                <NoResultsBoundary fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}>
                  <Hits
                    hitComponent={({ hit }: any) => (
                      <ProductTile
                        images={hit?.images}
                        product={mapProduct(hit, locale)}
                        isSearchResult={!!searchQuery}
                        isCategoryPage={true}
                      />
                    )}
                    classNames={{
                      root: searchQuery ? 'pt-32' : 'pt-32 lg:pt-0',
                      list: 'grid md:grid-cols-2 xl:grid-cols-3 gap-16 ',
                    }}
                  />
                   <Pagination
                    showFirst={false}
                    showLast={false}
                    padding={isTabletSize ? 3 : 1}
                    onClick={(event) => handleChange(event)}
                    classNames={{
                      root: 'flex justify-end mt-48 mb-48',
                      list: 'flex',
                      selectedItem: 'border !border-[#2B2C39] bg-[#DDDDDD] !text-[#174F1C]',
                      item: 'py-10 border border-[#DDDDDD] text-[#808080] text-14',
                      link: 'px-18 py-10',
                      disabledItem: 'hidden',
                    }}
                  /> 
                  

                </NoResultsBoundary>
              </>
            ) : (
              <NoResultsBoundary fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}>
                <InfiniteHits
                  showPrevious={false}
                  hitComponent={({ hit }: any) => (
                    <ProductTile
                      images={hit?.images}
                      product={mapProduct(hit, locale)}
                      isSearchResult={!!searchQuery}
                      isCategoryPage={true}
                    />
                  )}
                  classNames={{
                    root: searchQuery ? 'px-24 md:px-28 pt-32' : 'pt-32 lg:pt-0',
                    list: 'grid md:grid-cols-2 xl:grid-cols-3 gap-16 ',
                    loadMore:
                    currentPage < totalPages - 1
                        ? 'mt-48 border-1 rounded-none border border-[#2B2C39] bg-[#2B2C39] shadow-sm text-16 text-white hover:!bg-[#394A54] px-18 py-10 md:px-32 md:py-18 w-full'
                        : '!hidden',
                  }}
                  translations={{
                    showMoreButtonText: formatProductMessage({ id: 'show.more', defaultMessage: 'Show More' }),
                  }}
                />
              </NoResultsBoundary>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
export default ProductListAlgolia;